import React from 'react';

import {
  ButtonProps,
  CircularProgress,
  Button as MaterialButton,
} from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import clsx from 'clsx';

import { useStyles } from './AppButton.styles';

interface ICustomButtonProps extends ButtonProps {
  icon?: React.ReactNode;
  loading?: boolean;
}

export type SemanticAppButtonProps = Omit<
  ICustomButtonProps,
  'variant' | 'color' | 'icon' | 'startIcon'
>;

const ACTION_ICON_SX = { fontSize: 20 } as const;

export function AppButton({
  icon,
  loading = false,
  children,
  variant = 'contained',
  color = 'primary',
  className,
  startIcon,
  ...props
}: ICustomButtonProps) {
  const { classes } = useStyles({ variant, color });
  const resolvedStartIcon = icon ?? startIcon;
  const resolvedLoadingStartIcon = loading ? (
    <CircularProgress className={classes.circularProgress} size={24} />
  ) : (
    resolvedStartIcon
  );

  return (
    <MaterialButton
      variant={variant}
      color={color}
      className={clsx(classes.button, className)}
      {...props}
      startIcon={resolvedLoadingStartIcon}
      disabled={props.disabled || loading}>
      <span className={classes.labelWrap}>{children}</span>
    </MaterialButton>
  );
}

export function AppSaveButton(props: SemanticAppButtonProps) {
  return (
    <AppButton
      {...props}
      variant="contained"
      color="primary"
      icon={<CheckIcon sx={ACTION_ICON_SX} />}
    />
  );
}

export function AppCancelButton(props: SemanticAppButtonProps) {
  return (
    <AppButton
      {...props}
      variant="outlined"
      color="primary"
      icon={<CloseIcon sx={ACTION_ICON_SX} />}
    />
  );
}

export function AppDeleteButton(props: SemanticAppButtonProps) {
  return (
    <AppButton
      {...props}
      variant="outlined"
      color="error"
      icon={<DeleteOutlineOutlinedIcon sx={ACTION_ICON_SX} />}
    />
  );
}

export function AppEditButton(props: SemanticAppButtonProps) {
  return (
    <AppButton
      {...props}
      variant="contained"
      color="primary"
      icon={<EditOutlinedIcon sx={ACTION_ICON_SX} />}
    />
  );
}

export default AppButton;
